import { accessibleName, computeRole, headingLevel, INTERACTIVE_ROLES, STRUCTURAL_KEEP, } from "./roles.js";
const FORM_CONTROL_TAGS = new Set(["input", "textarea", "select"]);
function isHidden(el) {
    if (el.getAttribute("aria-hidden") === "true")
        return true;
    const he = el;
    if (he.hidden)
        return true;
    const style = el.ownerDocument?.defaultView?.getComputedStyle(el);
    if (style) {
        if (style.display === "none" || style.visibility === "hidden")
            return true;
        if (style.opacity === "0")
            return true;
    }
    return false;
}
function readStates(el, role) {
    const s = {};
    const he = el;
    if (he.disabled || el.getAttribute("aria-disabled") === "true")
        s.disabled = true;
    if (el.getAttribute("aria-expanded") != null)
        s.expanded = el.getAttribute("aria-expanded") === "true";
    if (role === "checkbox" || role === "radio" || role === "switch") {
        s.checked =
            he.checked === true || el.getAttribute("aria-checked") === "true" || undefined;
    }
    if (el.getAttribute("aria-selected") != null)
        s.selected = el.getAttribute("aria-selected") === "true";
    if (he.required || el.getAttribute("aria-required") === "true")
        s.required = true;
    if (el.getAttribute("aria-invalid") === "true" ||
        el.validity?.valid === false)
        s.invalid = true;
    if (el.ownerDocument.activeElement === el)
        s.focused = true;
    return s;
}
function controlValue(el, role) {
    const tag = el.tagName.toLowerCase();
    if (tag === "input") {
        const input = el;
        if (input.type === "password")
            return undefined; // never surface secrets
        if (input.type === "checkbox" || input.type === "radio")
            return undefined;
        return input.value ?? "";
    }
    if (tag === "textarea")
        return el.value ?? "";
    if (tag === "select") {
        const sel = el;
        return sel.selectedOptions[0]?.textContent?.trim() ?? "";
    }
    if (role === "textbox" && el.isContentEditable) {
        return (el.textContent ?? "").replace(/\s+/g, " ").trim().slice(0, 200);
    }
    return undefined;
}
function inDeniedZone(el, ctx) {
    for (const denied of ctx.denyEls) {
        if (denied === el || denied.contains(el))
            return true;
    }
    return false;
}
/** Depth-first build of a filtered snapshot node (or null if dropped). */
function build(el, ctx) {
    if (isHidden(el) || inDeniedZone(el, ctx))
        return null;
    const role = computeRole(el);
    const name = accessibleName(el) || undefined;
    const testid = el.getAttribute("data-testid") ??
        el.getAttribute("data-test-id") ??
        el.getAttribute("data-test") ??
        undefined;
    const children = [];
    for (const child of Array.from(el.children)) {
        if (ctx.count >= ctx.maxNodes) {
            ctx.omitted++;
            continue;
        }
        const built = build(child, ctx);
        if (built)
            children.push(built);
    }
    // Traverse an open shadow root, if present.
    const shadow = el.shadowRoot;
    if (shadow) {
        for (const child of Array.from(shadow.children)) {
            if (ctx.count >= ctx.maxNodes) {
                ctx.omitted++;
                continue;
            }
            const built = build(child, ctx);
            if (built)
                children.push(built);
        }
    }
    const tag = el.tagName.toLowerCase();
    const interactiveEl = INTERACTIVE_ROLES.has(role) ||
        FORM_CONTROL_TAGS.has(tag) ||
        el.tabIndex >= 0;
    const named = Boolean(name);
    const structural = STRUCTURAL_KEEP.has(role);
    // Collapse purely presentational wrappers: generic, unnamed, no testid,
    // exactly one child -> hoist the child.
    if (role === "generic" && !named && !testid) {
        if (children.length === 1)
            return children[0];
        if (children.length === 0)
            return null;
    }
    // In interactive mode, drop non-interactive, non-structural containers that
    // carry no direct interactive purpose but keep their interactive descendants.
    if (ctx.interactive && !interactiveEl && !structural) {
        if (children.length === 0)
            return null;
        if (role === "generic" && !named) {
            // Hoist children up (flatten noise).
            return children.length === 1
                ? children[0]
                : { role: "group", states: {}, children };
        }
    }
    const states = readStates(el, role);
    const value = controlValue(el, role);
    const node = {
        role,
        name,
        value,
        testid,
        states,
        children,
        level: headingLevel(el),
    };
    // Assign a ref to anything actionable or explicitly identifiable.
    if (interactiveEl || testid || structural) {
        node.ref = ctx.registry.refFor(el);
    }
    ctx.count++;
    return node;
}
function serialize(nodes, depth = 0) {
    const lines = [];
    const pad = "  ".repeat(depth);
    for (const n of nodes) {
        const parts = [`- ${n.role}`];
        if (n.name)
            parts.push(`"${n.name}"`);
        if (n.level)
            parts.push(`(h${n.level})`);
        if (n.ref)
            parts.push(`[${n.ref}]`);
        if (n.testid)
            parts.push(`testid=${n.testid}`);
        if (n.value !== undefined && n.value !== "")
            parts.push(`value=${JSON.stringify(n.value)}`);
        else if (n.value === "")
            parts.push(`value=""`);
        const st = Object.entries(n.states)
            .filter(([, v]) => v)
            .map(([k]) => k);
        if (st.length)
            parts.push(st.join(" "));
        lines.push(pad + parts.join(" "));
        if (n.children.length)
            lines.push(...serialize(n.children, depth + 1));
    }
    return lines;
}
export class SnapshotEngine {
    root;
    registry;
    policy;
    constructor(root, registry, policy) {
        this.root = root;
        this.registry = registry;
        this.policy = policy;
    }
    snapshot(opts = {}) {
        const rootEl = this.root();
        const doc = rootEl.ownerDocument;
        const denyEls = new Set();
        for (const sel of this.policy.zones.deny) {
            doc.querySelectorAll(sel).forEach((e) => denyEls.add(e));
        }
        let scopeEl = rootEl;
        if (opts.scope) {
            const el = this.registry.resolve(opts.scope);
            if (el)
                scopeEl = el;
        }
        else if (this.policy.zones.allowOnly?.length) {
            const first = this.policy.zones.allowOnly
                .map((s) => doc.querySelector(s))
                .find(Boolean);
            if (first)
                scopeEl = first;
        }
        const ctx = {
            registry: this.registry,
            policy: this.policy,
            interactive: opts.mode === "interactive",
            maxNodes: opts.maxNodes ?? 400,
            count: 0,
            omitted: 0,
            denyEls,
        };
        const top = build(scopeEl, ctx);
        const nodes = top ? (top.role === "group" && !top.name ? top.children : [top]) : [];
        const lines = serialize(nodes);
        let text = lines.join("\n");
        if (ctx.omitted > 0) {
            text += `\n…truncated: ${ctx.omitted} nodes omitted — scope with read_page(ref) or use interactive mode`;
        }
        return { nodes, text, truncated: ctx.omitted > 0, omitted: ctx.omitted };
    }
}
//# sourceMappingURL=snapshot.js.map